import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import {
  FaChevronDown,
  FaExternalLinkAlt,
  FaFileExcel,
  FaFileExport,
  FaFilePdf,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPrice } from '../../helpers/utility';
import {
  getDocumentPaymentHistory,
  searchFinancialDocuments,
} from '../../services/documentPaymentHistoryService';
import './DocumentPaymentHistoryPage.css';

const typeOptions = {
  CLIENT: [
    { value: 'BON_LIVRAISON', label: 'BL' },
    { value: 'FACTURE', label: 'Facture client' },
  ],
  FOURNISSEUR: [
    { value: 'BON_RECEPTION', label: 'BR' },
    { value: 'FACTURE_ACHAT', label: 'Facture fournisseur' },
  ],
};

const tabs = [
  { value: 'OVERVIEW', label: "Vue d'ensemble" },
  { value: 'PAYMENTS', label: 'Règlements' },
  { value: 'HISTORY', label: 'Historique complet' },
];

const money = value => formatPrice(Number(value || 0));
const date = value => (value ? new Date(value).toLocaleDateString('fr-FR') : '-');
const dateTime = value => (value ? new Date(value).toLocaleString('fr-FR') : '-');
const typeLabel = type => ({
  BON_LIVRAISON: 'BL',
  BON_RECEPTION: 'BR',
  FACTURE: 'Facture client',
  FACTURE_ACHAT: 'Facture fournisseur',
}[type] || type);
const eventLabel = type => ({
  CREATION: 'Création',
  PAYMENT: 'Règlement',
  PAIEMENT: 'Règlement',
  RETURN: 'Retour',
  RETOUR: 'Retour',
  CREDIT_NOTE: 'Avoir',
  AVOIR: 'Avoir',
  TRANSFER: 'Transfert',
  TRANSFERT: 'Transfert',
  MODIFICATION: 'Modification',
  CANCELLATION: 'Annulation',
  ANNULATION: 'Annulation',
}[type] || type || '-');

const DocumentPaymentHistoryPage = ({ forcedDomain }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialDomain = (forcedDomain || params.get('domain') || 'CLIENT').toUpperCase();
  const [domain, setDomain] = useState(initialDomain);
  const [documentType, setDocumentType] = useState(() => {
    const requestedType = params.get('type');
    return typeOptions[initialDomain].some(option => option.value === requestedType)
      ? requestedType
      : typeOptions[initialDomain][0].value;
  });
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [eventFilter, setEventFilter] = useState('TOUS');
  const [referenceFilter, setReferenceFilter] = useState('');
  const [calculationOpen, setCalculationOpen] = useState(false);

  useEffect(() => {
    const id = params.get('id');
    if (!id) return;
    setLoading(true);
    setError('');
    getDocumentPaymentHistory({ domain, documentType, documentId: id })
      .then(response => setHistory(response.data))
      .catch(err => setError(err.response?.data?.message || 'Impossible de calculer la situation financière.'))
      .finally(() => setLoading(false));
  }, [domain, documentType, params]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      searchFinancialDocuments({ domain, documentType, query: query.trim(), limit: 20 })
        .then(response => setResults(response.data || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [domain, documentType, query]);

  const changeDomain = value => {
    setDomain(value);
    setDocumentType(typeOptions[value][0].value);
    setHistory(null);
    setResults([]);
    setParams({ domain: value, type: typeOptions[value][0].value });
  };

  const changeDocumentType = value => {
    setDocumentType(value);
    setHistory(null);
    setResults([]);
    setParams(forcedDomain ? { type: value } : { domain, type: value });
  };

  const selectDocument = document => {
    setQuery('');
    setResults([]);
    setHistory(null);
    setActiveTab('OVERVIEW');
    setCalculationOpen(false);
    setParams(forcedDomain
      ? { type: document.documentType, id: String(document.id) }
      : { domain: document.domain, type: document.documentType, id: String(document.id) });
  };

  const changeDocument = () => {
    setHistory(null);
    setError('');
    setQuery('');
    setResults([]);
    setParams(forcedDomain ? { type: documentType } : { domain, type: documentType });
  };

  const filteredEvents = useMemo(() => (history?.events || []).filter(event => {
    const categoryMatch = eventFilter === 'TOUS'
      || (eventFilter === 'FINANCIER' && event.effective)
      || event.category === eventFilter;
    const referenceMatch = !referenceFilter
      || `${event.reference || ''} ${event.relatedDocument || ''} ${event.description || ''}`
        .toLowerCase()
        .includes(referenceFilter.toLowerCase());
    return categoryMatch && referenceMatch;
  }), [eventFilter, history, referenceFilter]);

  const exportRows = () => filteredEvents.map(event => ({
    Date: dateTime(event.dateTime),
    Type: eventLabel(event.eventType),
    Référence: event.reference,
    Document: event.relatedDocument,
    Description: event.description,
    'Effet financier': Number(event.financialImpact || 0),
    'Montant appliqué': Number(event.attributedAmount || 0),
    'Reste après opération': Number(event.balanceAfter || 0),
    Statut: event.status,
    Utilisateur: event.user,
  }));

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([{
      Document: `${typeLabel(history.documentType)} · ${history.reference}`,
      Tiers: history.partyName,
      Société: history.companyName,
      'Montant net': Number(history.netAmount),
      'Total réglé': Number(history.totalUniquePaidAmount),
      'Reste à payer': Number(history.remainingAmount),
      'Avance disponible': Number(history.availableExcessAmount),
    }]), 'Synthèse');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportRows()), 'Historique');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer]), `Historique_reglements_${history.reference}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text(`Historique des règlements — ${history.reference}`, 14, 14);
    doc.text(`${history.partyName || ''} — Reste à payer : ${money(history.remainingAmount)}`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [['Date', 'Type', 'Référence', 'Description', 'Montant appliqué', 'Reste', 'Statut']],
      body: filteredEvents.map(event => [
        dateTime(event.dateTime),
        eventLabel(event.eventType),
        event.reference || '-',
        event.description || '-',
        money(event.attributedAmount),
        money(event.balanceAfter),
        event.status || '-',
      ]),
    });
    doc.save(`Historique_reglements_${history.reference}.pdf`);
  };

  const originalPath = history?.documentType === 'BON_LIVRAISON'
    ? '/sales-module/delivery'
    : history?.documentType === 'BON_RECEPTION'
      ? '/purchases-module/reception'
      : history?.documentType === 'FACTURE'
        ? '/sales-module/invoices'
        : '/purchases-module/invoices';
  const isPaid = Number(history?.remainingAmount || 0) <= 0;
  const paymentSources = history?.paymentSources || [];
  const emptyPaymentMessage = `Aucun règlement lié à ce ${typeLabel(history?.documentType)}`;

  return (
    <div className="document-history-page">
      <Card className="document-history-hero mb-3">
        <CardBody>
          <h3>Historique des règlements par document</h3>
          <p className="mb-0">Situation de paiement et traçabilité financière d’un document.</p>
        </CardBody>
      </Card>

      {!history && !loading && (
        <Card className="document-history-search-card mb-3">
          <CardBody>
            <Row className="g-3 align-items-end">
              {!forcedDomain && (
                <Col lg={2} md={4}>
                  <Label>Domaine</Label>
                  <Input type="select" value={domain} onChange={event => changeDomain(event.target.value)}>
                    <option value="CLIENT">Clients</option>
                    <option value="FOURNISSEUR">Fournisseurs</option>
                  </Input>
                </Col>
              )}
              <Col lg={3} md={4}>
                <Label>Type de document</Label>
                <Input type="select" value={documentType} onChange={event => changeDocumentType(event.target.value)}>
                  {typeOptions[domain].map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Input>
              </Col>
              <Col lg={forcedDomain ? 9 : 7} md={forcedDomain ? 8 : 4}>
                <Label>Document</Label>
                <div className="position-relative">
                  <div className="input-group">
                    <span className="input-group-text"><FaSearch /></span>
                    <Input
                      value={query}
                      onChange={event => setQuery(event.target.value)}
                      placeholder="Référence exacte ou partielle…"
                    />
                    {searching && <span className="input-group-text"><Spinner size="sm" /></span>}
                  </div>
                  {results.length > 0 && (
                    <div className="list-group document-history-search-results">
                      {results.map(item => (
                        <button
                          type="button"
                          className="list-group-item list-group-item-action document-history-result"
                          key={`${item.documentType}-${item.id}`}
                          onClick={() => selectDocument(item)}
                        >
                          <strong>{typeLabel(item.documentType)} · {item.reference}</strong>
                          <div className="small text-muted">
                            {item.partyName || '-'} · {date(item.date)} · {item.companyName || '-'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            {query.trim().length >= 2 && !searching && results.length === 0 && (
              <div className="document-history-empty-search">Aucun document trouvé pour cette recherche.</div>
            )}
          </CardBody>
        </Card>
      )}

      {error && <Alert color="danger">{error}</Alert>}
      {loading && <Alert color="info"><Spinner size="sm" className="me-2" />Calcul de la situation financière…</Alert>}

      {history && !loading && (
        <>
          {(history.warnings || []).map(warning => <Alert color="warning" key={warning}>{warning}</Alert>)}

          <Card className="document-history-document-card mb-3">
            <CardBody>
              <div className="document-history-document-header">
                <div className="document-history-identity">
                  <div className="document-history-eyebrow">{typeLabel(history.documentType)}</div>
                  <h4>{history.reference}</h4>
                  <div className="document-history-party">
                    {history.partyName || '-'} · {date(history.documentDate)} · {history.companyName || '-'}
                  </div>
                  <div className="document-history-statuses">
                    <Badge color="secondary">Document : {history.lifecycleStatus || '-'}</Badge>
                    {history.invoicingStatus && <Badge color="info">Facturation : {history.invoicingStatus}</Badge>}
                    <Badge color={isPaid ? 'success' : 'warning'}>Paiement : {history.paymentStatus || '-'}</Badge>
                  </div>
                </div>
                <div className={`document-history-primary-answer ${isPaid ? 'is-paid' : 'is-due'}`}>
                  <span>Reste à payer</span>
                  <strong>{money(history.remainingAmount)}</strong>
                  <small>{isPaid ? 'Document entièrement réglé' : 'Montant encore dû'}</small>
                </div>
                <div className="document-history-actions">
                  <Button color="primary" onClick={() => navigate(originalPath)}>
                    <FaExternalLinkAlt /> Ouvrir le document
                  </Button>
                  <UncontrolledDropdown>
                    <DropdownToggle outline color="secondary" caret>
                      <FaFileExport /> Exporter
                    </DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem onClick={exportExcel}><FaFileExcel /> Excel</DropdownItem>
                      <DropdownItem onClick={exportPdf}><FaFilePdf /> PDF</DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                  <Button outline color="secondary" onClick={changeDocument}>Changer de document</Button>
                </div>
              </div>
            </CardBody>
          </Card>

          <div className="document-history-summary mb-3">
            {[
              ['Montant net', history.netAmount],
              ['Total réglé', history.totalUniquePaidAmount, 'is-paid'],
              ['Reste à payer', history.remainingAmount, isPaid ? 'is-paid' : 'is-due'],
              ['Avance disponible', history.availableExcessAmount, 'is-advance'],
            ].map(([label, value, css]) => (
              <div className={`document-history-metric ${css || ''}`} key={label}>
                <span>{label}</span>
                <strong>{money(value)}</strong>
              </div>
            ))}
          </div>

          <div className="document-history-tabs" role="tablist" aria-label="Sections de l’historique">
            {tabs.map(tab => (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === tab.value}
                className={activeTab === tab.value ? 'active' : ''}
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'OVERVIEW' && (
            <Card className="document-history-tab-card">
              <CardBody>
                <button
                  type="button"
                  className="document-history-calculation-toggle"
                  aria-expanded={calculationOpen}
                  onClick={() => setCalculationOpen(value => !value)}
                >
                  <span><strong>Détail du calcul</strong><small>Origine du montant net et des règlements</small></span>
                  <FaChevronDown className={calculationOpen ? 'is-open' : ''} />
                </button>
                {calculationOpen && (
                  <div className="document-history-calculation">
                    {[
                      ['Montant original', history.originalAmount],
                      ['Retours et avoirs', history.returnsAndCreditsAmount],
                      ['Règlements directs', history.directPaidAmount],
                      ['Règlements via facture', history.paidThroughInvoicesAmount],
                      ['Avances utilisées', history.advanceAppliedAmount],
                    ].map(([label, value]) => (
                      <div key={label}><span>{label}</span><strong>{money(value)}</strong></div>
                    ))}
                  </div>
                )}
                <div className="document-history-explanation">
                  Le total réglé utilise le montant unique calculé par le système afin de conserver la traçabilité sans compter deux fois un règlement transféré vers une facture.
                </div>
              </CardBody>
            </Card>
          )}

          {activeTab === 'PAYMENTS' && (
            <Card className="document-history-tab-card">
              <CardBody>
                <h5>Règlements liés au document</h5>
                {paymentSources.length === 0 ? (
                  <div className="document-history-empty-state">{emptyPaymentMessage}</div>
                ) : (
                  <div className="table-responsive document-history-table">
                    <Table hover>
                      <thead><tr><th>Règlement</th><th>Date</th><th>Mode</th><th>Origine</th><th className="text-end">Montant attribué</th><th>Statut</th></tr></thead>
                      <tbody>
                        {paymentSources.map(source => (
                          <tr key={source.allocationId || `${source.paymentReference}-${source.attributedAmount}`}>
                            <td className="fw-semibold">{source.paymentReference || '-'}</td>
                            <td>{date(source.paymentDate)}</td>
                            <td>{source.method || '-'}</td>
                            <td className="document-history-origin">{source.route || 'Règlement direct'}</td>
                            <td className="text-end fw-bold">{money(source.attributedAmount)}</td>
                            <td><Badge color={source.allocationStatus === 'ACTIVE' ? 'success' : 'secondary'}>{source.paymentStatus || source.allocationStatus || '-'}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {activeTab === 'HISTORY' && (
            <Card className="document-history-tab-card">
              <CardBody>
                <div className="document-history-history-header">
                  <h5>Historique chronologique complet</h5>
                  <div className="document-history-filters">
                    <Input type="select" value={eventFilter} onChange={event => setEventFilter(event.target.value)}>
                      <option value="TOUS">Tous les événements</option>
                      <option value="FINANCIER">Mouvements financiers</option>
                      <option value="DOCUMENT">Documents liés</option>
                      <option value="MODIFICATION">Modifications et annulations</option>
                    </Input>
                    <Input value={referenceFilter} onChange={event => setReferenceFilter(event.target.value)} placeholder="Référence ou description…" />
                    {(eventFilter !== 'TOUS' || referenceFilter) && (
                      <Button outline aria-label="Effacer les filtres" onClick={() => { setEventFilter('TOUS'); setReferenceFilter(''); }}><FaTimes /></Button>
                    )}
                  </div>
                </div>
                {filteredEvents.length === 0 ? (
                  <div className="document-history-empty-state">Aucun événement à afficher pour ce document.</div>
                ) : (
                  <div className="table-responsive document-history-table">
                    <Table hover>
                      <thead><tr><th>Date</th><th>Événement</th><th>Référence</th><th>Description</th><th className="text-end">Effet financier</th><th className="text-end">Montant appliqué</th><th className="text-end">Reste après opération</th><th>Statut</th><th>Utilisateur</th></tr></thead>
                      <tbody>
                        {filteredEvents.map(event => (
                          <tr key={`${event.id}-${event.eventType}`} className={!event.effective && event.eventType !== 'CREATION' ? 'table-light text-muted' : ''}>
                            <td>{dateTime(event.dateTime)}</td>
                            <td>{eventLabel(event.eventType)}</td>
                            <td>{event.reference || '-'}</td>
                            <td>{event.description || '-'}</td>
                            <td className="text-end">{money(event.financialImpact)}</td>
                            <td className="text-end">{money(event.attributedAmount)}</td>
                            <td className="text-end fw-bold">{money(event.balanceAfter)}</td>
                            <td>{event.status || '-'}</td>
                            <td>{event.user || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                <div className="document-history-convention">
                  Le reste commence au montant net du document et diminue uniquement avec les mouvements financiers effectifs.
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentPaymentHistoryPage;
